import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import supabase from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/common/Logo';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role } = useAuth();

  const ADMIN_EMAIL = import.meta.env.VITE_ADMIN_EMAIL || 'blogs.gpt5@gmail.com';

  useEffect(() => {
    if (user && role === 'admin') {
      const origin = location.state?.from?.pathname || '/admin';
      navigate(origin);
    }
  }, [user, role, navigate, location]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const inputEmail = email.trim().toLowerCase();
    const targetEmail = ADMIN_EMAIL.trim().toLowerCase();

    if (inputEmail !== targetEmail) {
      setError('Access Denied: This account is not authorized.');
      setLoading(false);
      return;
    }

    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: inputEmail,
        password: password,
      });
      if (authError) throw authError;
    } catch (err) {
      setError(err.message === 'Invalid login credentials' ? 'Incorrect password.' : err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-brand-bg">
      <div className="editorial-card max-w-sm w-full p-8 shadow-2xl">
        <div className="text-center mb-8">
          {/* Increased Logo Size */}
          <Logo className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-[20px] font-bold text-brand-text">Editor Access</h1>
          <p className="text-brand-secondary text-[14px] mt-1">Pulse5 Management</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-[11px] font-mono font-bold text-brand-muted uppercase mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-brand-bg border border-brand-border rounded-[8px] px-4 py-3 text-[14px] text-brand-text focus:outline-none focus:border-brand-mint transition-colors"
            />
          </div>
          <div>
            <label className="block text-[11px] font-mono font-bold text-brand-muted uppercase mb-2">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-brand-bg border border-brand-border rounded-[8px] px-4 py-3 text-[14px] text-brand-text focus:outline-none focus:border-brand-mint transition-colors"
            />
          </div>

          {error && (
            <div className="p-3 rounded-[6px] bg-brand-breaking/10 border border-brand-breaking/20 text-brand-breaking text-[13px] font-medium">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 mt-2 bg-brand-text text-brand-bg font-bold rounded-[8px] hover:bg-brand-secondary transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-[14px]"
          >
            {loading ? <SafeIcon icon="Loader" className="w-4 h-4 animate-spin" /> : 'Authenticate'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;